import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { subscribeToPush } from '../utils/pushService';
import { savePushSubscription } from '../utils/notificationService';

const PushNotificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    // Check if we should show the prompt
    const checkPermissions = async () => {
      // Don't show if already decided
      if (localStorage.getItem('pushPromptAnswered')) return;
      
      // Don't show if not supported
      if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

      // Don't show if already granted
      if (Notification.permission === 'granted') {
        localStorage.setItem('pushPromptAnswered', 'true');
        return;
      }

      // Don't show if already denied
      if (Notification.permission === 'denied') {
        localStorage.setItem('pushPromptAnswered', 'true');
        return;
      }

      // Show prompt after 5 seconds on dashboard
      setTimeout(() => {
        setShowPrompt(true);
      }, 5000);
    };

    checkPermissions();
  }, []);

  const handleEnable = async () => {
    setRequesting(true);
    
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        // Subscribe to push notifications
        const subscription = await subscribeToPush();
        
        if (subscription) {
          // Save subscription to Supabase
          const userEmail = localStorage.getItem('userEmail');
          if (userEmail) {
            await savePushSubscription(userEmail, subscription.toJSON());
          }
        }
        
        localStorage.setItem('pushPromptAnswered', 'true');
        setShowPrompt(false);
      } else {
        localStorage.setItem('pushPromptAnswered', 'true');
        setShowPrompt(false);
      }
    } catch (error) {
      console.error('Push notification setup failed:', error);
    } finally {
      setRequesting(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pushPromptAnswered', 'true');
    setShowPrompt(false);
  };

  return (
    <AnimatePresence>
      {showPrompt && (
        <>
          {/* Backdrop with centering */}
          <div 
            className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" 
            onClick={handleDismiss}
          >
            {/* Prompt Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
            {/* Icon */}
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full mb-3">
                <span className="text-3xl">🔔</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Stay Connected
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Get instant notifications when members post in the community or when someone likes your posts. Never miss important updates!
              </p>
            </div>

            {/* Benefits */}
            <div className="space-y-2 mb-6">
              <div className="flex items-center text-sm text-gray-700">
                <span className="mr-2">💬</span>
                <span>New community posts</span>
              </div>
              <div className="flex items-center text-sm text-gray-700">
                <span className="mr-2">❤️</span>
                <span>When your posts get likes</span>
              </div>
              <div className="flex items-center text-sm text-gray-700">
                <span className="mr-2">📱</span>
                <span>Works on your phone's lock screen</span>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={handleEnable}
                disabled={requesting}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50"
              >
                {requesting ? 'Enabling...' : 'Enable Notifications'}
              </button>
              <button
                onClick={handleDismiss}
                className="w-full text-gray-600 py-2 text-sm hover:text-gray-800 transition-colors"
              >
                Not now
              </button>
            </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PushNotificationPrompt;
